import React, { useEffect, useState } from "react";
import styles from './Reports.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import PageTitle from "../../../components/pageTitle/PageTitle";
import Dropdown from "../../../components/dropdown/Dropdown";
import axiosClient from "../../../api/axiosClient";
import TextField from "../../../components/textField/TextField";
import CustomDatePicker from "../../../components/customDatePicker/CustomDatePicker";
import Button from "../../../components/button/Button";
import { format } from "date-fns";
import { useToasts } from "../../../contexts/ToastContext";
import { useProgram } from "../../../contexts/ProgramContext";
import MultiSelectDropdown from "../../../components/multiSelectDropdown/MultiSelectDropdown";
import DropDownButton from "../../../components/dropDownButton/DropDownButton";

const Reports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [type, setType] = useState('SCES');
  const [studentNameList, setStudentNameList] = useState([]);
  const [studentNameId, setStudentNameId] = useState(null);
  const [studentId, setStudentId] = useState('');
  const [programYearList, setProgramYearList] = useState([]);
  const [programYearId, setProgramYearId] = useState(null);
  const [semesterId, setSemesterId] = useState(null);
  const [specialitiesList, setSpecialitiesList] = useState([]);
  const [specialityIds, setSpecialityIds] = useState([]);
  const [clinicalSiteList, setClinicalSiteList] = useState([]);
  const [clinicalSiteIds, setClinicalSiteIds] = useState([]);
  const [instructorList, setInstructorList] = useState([]);
  const [instructorIds, setInstructorIds] = useState([]);
  const [evaluationDate, setEvaluationDate] = useState(null);

  const { programs: programList, selectedProgramId, setSelectedProgramId } = useProgram();

  // Case Log Report fields
  const [caseLogStartDate, setCaseLogStartDate] = useState(null);
  const [caseLogEndDate, setCaseLogEndDate] = useState(null);

  const { showToast } = useToasts();

  useEffect(() => {
    if (!selectedProgramId) return;
    axiosClient.get(`/programs/${selectedProgramId}/students`).then((response) => {
      const students = response.data?.data || response.data?.students || [];
      setStudentNameList(students.map((item) => ({
        value: item.id,
        label: `${item.firstName} ${item.lastName}`
      })));
    });
  }, [selectedProgramId]);

  useEffect(() => {
    if (!selectedProgramId) return;
    axiosClient.get('years/details', { params: { programId: selectedProgramId } }).then((response) => {
      setProgramYearList(response.data.map((item) => ({
        value: item.year.id,
        label: item.year.year,
        semesters: item.semesters
      })));
    }).catch((error) => { showToast(error.message, 'error'); });
  }, [selectedProgramId, showToast]);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`specialities`)
      .then((response) => {
        if(response.data) {
          setSpecialitiesList(response.data.map((item) => ({
            value: item.id,
            label: item.name
          })));
        }
      })
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`clinical-sites/findAll`)
      .then((response) => {
        setClinicalSiteList(response.data.map((item) => ({
          value: item.id,
          label: item.name
        })));
      })
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`users/instructors`)
      .then((response) => {
        if(response.data?.instructors) {
          setInstructorList(response.data.instructors.map((item) => ({
            value: item.id,
            label: `${item.firstName} ${item.lastName}`
          })));
        }
      })
    };

    fetchData();
  }, []);

  const getSemesters = () => {
    if(programYearId) {
      const selectedYearData = programYearList.find(item => item.value === parseInt(programYearId));
      if(selectedYearData?.semesters) {
        return selectedYearData.semesters.map((item) => ({
          value: item.id,
          label: item.semester
        }));
      }
    }

    return [];
  }

  const clearForm = () => {
    setStudentNameId(null);
    setStudentId('');
    setProgramYearId(null);
    setSemesterId(null);
    setSpecialityIds([]);
    setClinicalSiteIds([]);
    setInstructorIds([]);
    setEvaluationDate(null);
    setCaseLogStartDate(null);
    setCaseLogEndDate(null);
  }

  const handleCaseLogSubmit = async () => {
    setIsLoading(true);
    const params = {
      programId: selectedProgramId,
      startDate: format(caseLogStartDate, 'yyyy-MM-dd'),
      endDate: format(caseLogEndDate, 'yyyy-MM-dd'),
    };

    await axiosClient.post('reports/case-log-report', params, {
      responseType: 'blob',
      transformResponse: [(data) => data],
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'case_log_report.xlsx';
      link.click();
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  const hanldeSubmit = async () => {
    if (type === 'CLR') {
      return handleCaseLogSubmit();
    }

    setIsLoading(true);
    const params = {};
    if(selectedProgramId) {
      params.programId = selectedProgramId;
    }
    if(studentNameId) {
      params.userId = studentNameId;
    }
    if(studentId) {
      params.studentId = studentId;
    }
    if(programYearId) {
      params.year = programYearId;
    }
    if(semesterId) {
      params.semester = semesterId;
    }
    if(evaluationDate) {
      params.evaluationDate = format(evaluationDate, 'yyyy-MM-dd');
    }
    if(specialityIds.length > 0) {
      params.speciality = specialityIds
    }
    if(clinicalSiteIds.length > 0) {
      params.clinicalSite = clinicalSiteIds;
    }
    if(instructorIds.length > 0) {
      params.instructor = instructorIds;
    }
    const link = document.createElement("a");
    if(type === 'SCES') {
      link.download = "student_cohort_evaluation_summary.xlsx";
      params.reportName='STUDENT_COHORT_EVALUATION_SUMMARY';
    }
    if(type === 'CES') {
      link.download = "cohort_evaluation_summary.xlsx";
      params.reportName='COHORT_EVALUATION_SUMMARY'
    }
    if(type === 'SES') {
      link.download = "student_evaluation_summary.xlsx";
      params.reportName = 'STUDENT_EVALUATION_SUMMARY';
    }
    if(type === 'SE') {
      link.download = "student_evaluation_details.xlsx";
      params.reportName = 'STUDENT_EVALUATION_REPORT';
    }
    if(type === 'AT') {
      link.download = "students_attendence_log.xlsx";
      params.reportName = 'STUDENT_ATTENDANCE_REPORT';
    }

    await axiosClient.post('reports/evaluation-reports', params, {
      responseType: 'blob',
      transformResponse: [(data) => data],
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response]));
      link.href = url;
      link.click();
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  const isCaseLogValid = selectedProgramId && caseLogStartDate && caseLogEndDate;
  const selectedProgramTitle = programList.find(p => p.id === selectedProgramId)?.title || 'Select Program';
  const programOptions = programList.map(p => ({ label: p.title, onClick: () => setSelectedProgramId(p.id) }));
  const isEvalValid = studentNameId || studentId || programYearId || semesterId ||
    specialityIds.length > 0 || clinicalSiteIds.length > 0 || instructorIds.length > 0 ||
    evaluationDate;

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <PageTitle label="Student & Cohort Evaluation Report" />
          <DropDownButton
            label={selectedProgramTitle}
            items={programOptions}
            type="button"
            size="small"
            variant="primary-light"
            position="bottom-left"
          />
        </div>
      </div>
      <div className={styles.container}>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            hanldeSubmit();
          }}
        >
          <div className={styles.row} style={{alignItems: 'center'}}>
            <div className={styles.column20}>
              <h4 className={styles.type}>Select Report Type</h4>
            </div>
            <div className={styles.column50}>
              <Dropdown
                options={[
                  {
                    value: 'SCES',
                    label: 'Student Cohort Evaluation Summary Report',
                  },
                  {
                    value: 'CES',
                    label: 'Cohort Evaluation Summary Report',
                  },
                  {
                    value: 'SES',
                    label: 'Student Evaluation Summary Report',
                  },
                  {
                    value: 'SE',
                    label: 'Student Evaluation Report',
                  },
                  {
                    value: 'AT',
                    label: 'Attendance',
                  },
                  {
                    value: 'CLR',
                    label: 'Case Log Report',
                  },
                ]}
                value={type}
                onChange={(selected) => {
                  setType(prevSelected => {
                    if(prevSelected !== selected) {
                      clearForm()
                    }
                    return selected;
                  })
                }}
              />
            </div>
          </div>
          <hr />
          <div className={styles.row}>
            {type !== 'CES' && type !== 'CLR' && <div className={styles.column50}>
              <Dropdown
                label="Student Name"
                options={studentNameList}
                value={studentNameId}
                onChange={(selected) => setStudentNameId(selected)}
                searchable={true}
              />
            </div>}
            {type !== 'CES' && type !== 'CLR' && <div className={styles.column50}>
              <TextField
                label="Student ID"
                value={studentId}
                onChange={(value) => setStudentId(value)}
              />
            </div>}
            {type !== 'SE' && type !== 'CLR' && <div className={styles.column50}>
              <Dropdown
                label="Program Year"
                options={programYearList}
                value={programYearId}
                onChange={(selected) => {
                  setProgramYearId(prevValue => {
                    if(prevValue !== selected) {
                      setSemesterId(null)
                    }
                    return selected;
                  })
                }}
              />
            </div>}
            {type !== 'SE' && type !== 'CLR' && <div className={styles.column50}>
              <Dropdown
                label="Semester"
                options={getSemesters()}
                value={semesterId}
                onChange={(selected) => setSemesterId(selected)}
              />
            </div>}
            {type === 'CES' && <div className={styles.column50}>
              <MultiSelectDropdown
                label="Specialty"
                options={specialitiesList}
                selected={specialityIds}
                onChange={(selected) => setSpecialityIds(selected)}
              />
            </div>}
            {type === 'CES' && <div className={styles.column50}>
              <MultiSelectDropdown
                label="Clinical Site"
                options={clinicalSiteList}
                selected={clinicalSiteIds}
                onChange={(selected) => setClinicalSiteIds(selected)}
              />
            </div>}
            {type === 'CES' && <div className={styles.column50}>
              <MultiSelectDropdown
                label="Instructor"
                options={instructorList}
                selected={instructorIds}
                onChange={(selected) => setInstructorIds(selected)}
                searchable={true}
              />
            </div>}
            {type === 'SE' && <div className={styles.column50}>
              <CustomDatePicker
                label="Evaluation Date"
                date={evaluationDate}
                onChange={(date) => setEvaluationDate(date)}
                showIcon={true}
                maxDate={new Date()}
              />
            </div>}

            {type === 'CLR' && <div className={styles.column25}>
              <CustomDatePicker
                label="From Date"
                date={caseLogStartDate}
                onChange={(date) => setCaseLogStartDate(date)}
                showIcon={true}
              />
            </div>}
            {type === 'CLR' && <div className={styles.column25}>
              <CustomDatePicker
                label="To Date"
                date={caseLogEndDate}
                onChange={(date) => setCaseLogEndDate(date)}
                showIcon={true}
                minDate={caseLogStartDate}
              />
            </div>}
          </div>
          <div className={styles.actions}>
            <Button
              label="Export Report"
              variant="primary"
              size="medium"
              type="submit"
              disabled={type === 'CLR' ? !isCaseLogValid : !isEvalValid}
            />
          </div>
        </form>
      </div>
    </>
  );
}

export default Reports;
